import ButtonIconVertical from '@/components/ButtonIconVertical';
import CustomCard from '@/components/customCard';
import ErrorHelperText from '@/components/ErrorHelperText';
import EstimatedDateSetup from '@/components/EstimatedDateSetup';
import EstimatedTextCard from '@/components/EstimatedTextCard';
import { BubbleInfoIcon } from '@/components/Icons';
import RegularText from '@/components/RegularText';
import TestEditor from '@/components/TestEditor';
import CardTooltip from '@/pages/Settings/Components/EstimateRules/components/CURule/components/DateAndMessage/components/CardTooltip';
import { clickedDiscardSelector } from '@/redux/slice/appbar.slice';
import ruleSlice, { IRuleErrorField, ruleErrorsSelector, ruleSettingsSelector } from '@/redux/slice/rule.slice';
import { rulesSelector } from '@/redux/slice/rules.slice';
import { IRule } from '@/types/newSetup.type';
import { BlockStack, Button, Card, Checkbox, DatePicker, Popover } from '@shopify/polaris';
import { CalendarIcon, PlusIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
const now = new Date();

const DisabledDayStyle = styled.div`
  .Polaris-DatePicker__Day--disabled {
    background-color: #fff !important;
    color: #ccc;
    cursor: not-allowed;
  }
`;

const DateAndMessage = ({
  setIsVisitedDateAndMessage,
}: {
  setIsVisitedDateAndMessage?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const ruleSettings = useSelector(ruleSettingsSelector);
  const clickedDiscard = useSelector(clickedDiscardSelector);
  const ruleErrors = useSelector(ruleErrorsSelector);
  const { allRules } = useSelector(rulesSelector);
  const [isShowCardTooltip, setIsShowCardTooltip] = useState(false);
  const [isShowAddTooltipBtn, setIsShowAddTooltipBtn] = useState(false);
  const [isInitShowCardTooltip, setIsInitShowCardTooltip] = useState(true);
  const [isShowSelectDateSetup, setIsShowSelectDateSetup] = useState(false);
  const [date, setDate] = useState({
    month: now.getMonth(),
    year: now.getFullYear(),
  });

  const currentRule = useMemo(() => {
    return allRules.find((rule) => rule.id === Number(id));
  }, [allRules, id]);

  useEffect(() => {
    setIsVisitedDateAndMessage?.(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ruleSettings.privacyPageText && isInitShowCardTooltip) {
      setIsShowCardTooltip(true);
      setIsInitShowCardTooltip(false);
    }
  }, [ruleSettings.privacyPageText, isInitShowCardTooltip]);

  useEffect(() => {
    if (!clickedDiscard) return;
    setIsShowCardTooltip(!!ruleSettings.privacyPageText);
    setIsShowAddTooltipBtn(!!ruleSettings.privacyPageText);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickedDiscard]);

  const handleAddField = () => {
    setIsShowCardTooltip(true);
  };

  const handleErrors = useCallback(
    (field: IRuleErrorField, msg: string) => {
      dispatch(ruleSlice.actions.handleSetError({ value: { field, msg } }));
    },
    [dispatch],
  );

  const handleUpdateValue = useCallback(
    (key: keyof IRule, value: IRule[keyof IRule]) => {
      dispatch(ruleSlice.actions.handleChange({ key, value }));
    },
    [dispatch],
  );

  const handleDeleteField = () => {
    setIsShowAddTooltipBtn(false);
    setIsShowCardTooltip(false);
    handleErrors('tooltipText', '');
    handleUpdateValue('privacyPageText', '');
    handleUpdateValue('privacyPageUrl', '');
    handleUpdateValue('privacyMore', '');
  };

  const handleAddFieldItems = () => {
    setIsShowAddTooltipBtn(true);
    handleErrors('tooltipText', 'This field is required');
  };

  useEffect(() => {
    if (ruleSettings.isUsePreOrderDate && !ruleSettings.preOrderDate) handleErrors('preOrderDate', 'Please select a date');
    else if (!ruleSettings.isUsePreOrderDate || (ruleSettings.isUsePreOrderDate && ruleSettings.preOrderDate))
      handleErrors('preOrderDate', '');
  }, [handleErrors, ruleSettings.isUsePreOrderDate, ruleSettings.preOrderDate]);

  useEffect(() => {
    if (!ruleSettings.isUsePreOrderDate) handleUpdateValue('preOrderDate', currentRule?.preOrderDate || null);
  }, [currentRule, handleUpdateValue, ruleSettings.isUsePreOrderDate]);

  return (
    <>
      <CustomCard hasDroplist required title="Rule name" textClassName="bold-text-styled">
        <div className="mt-8 mb-4">
          <RegularText>Name of delivery method, carrier, service type, or name for internal uses.</RegularText>
        </div>

        <Checkbox
          label="Show this rule name on store"
          checked={ruleSettings.isShowDeliveryMethodName}
          onChange={(value) => {
            handleUpdateValue('isShowDeliveryMethodName', value);
          }}
        />
        <div className="mt-8">
          <TestEditor
            isRequired
            value={ruleSettings.styledName || ruleSettings.name || ''}
            onBlur={(value, styledValue) => {
              handleUpdateValue('name', value);
              handleUpdateValue('styledName', styledValue);
            }}
            onChange={(_, styledValue) => {
              handleErrors('ruleName', styledValue?.trimStart() ? '' : 'This field is required');
            }}
            onlyShowEmojiInIconButton
            isHideTipsAndTemplate
            isHideVariableButton
            isHideAddImage
            defaultError={!!ruleErrors.find((error) => error.field === 'ruleName') ? 'This field is required' : ''}
          />
        </div>
      </CustomCard>

      <EstimatedDateSetup
        required
        orderToShipDays={ruleSettings.minPrepDays.toString()}
        onChangeOrderToShipDays={(newValue) => {
          handleUpdateValue('minPrepDays', newValue);
        }}
        prepDays={ruleSettings.maxPrepDays.toString()}
        onChangePrepDays={(newValue) => {
          handleUpdateValue('maxPrepDays', newValue);
        }}
        shipToReceiveDays={ruleSettings.minDeliveryDays.toString()}
        onChangeShipToReceiveDays={(newValue) => {
          handleUpdateValue('minDeliveryDays', newValue);
        }}
        estDays={ruleSettings.maxDeliveryDays.toString()}
        onChangeEstDays={(newValue) => {
          handleUpdateValue('maxDeliveryDays', newValue);
        }}
        additionalContent={
          <div className="mt-8">
            <BlockStack>
              <Checkbox
                label="Calculate from custom date (for pre-order, restock alert, scheduled releases)"
                checked={ruleSettings?.isUsePreOrderDate}
                onChange={(value) => handleUpdateValue('isUsePreOrderDate', value)}
                helpText="By default, the estimated date is calculated from the order date. Enable this option to override the default calculation with custom method."
              />
              {ruleSettings?.isUsePreOrderDate && (
                <div style={{ paddingLeft: '26px' }}>
                  <p className="mb-8">Calculate from a specific date</p>
                  <Popover
                    sectioned
                    captureOverscroll={false}
                    active={isShowSelectDateSetup}
                    activator={
                      <Button onClick={() => setIsShowSelectDateSetup((prev) => !prev)} icon={CalendarIcon}>
                        {ruleSettings?.preOrderDate
                          ? new Date(ruleSettings?.preOrderDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : 'Select'}
                      </Button>
                    }
                    onClose={() => {
                      setIsShowSelectDateSetup(false);
                    }}
                  >
                    <DisabledDayStyle>
                      <DatePicker
                        onMonthChange={(month: number, year: number) => setDate({ month, year })}
                        month={date.month}
                        year={date.year}
                        onChange={(value) => {
                          const newValue = value?.start?.toISOString();
                          const isUnSelect = newValue === ruleSettings?.preOrderDate;
                          handleUpdateValue('preOrderDate', isUnSelect ? null : newValue);
                        }}
                        selected={ruleSettings?.preOrderDate ? new Date(ruleSettings?.preOrderDate) : undefined}
                        disableDatesBefore={now}
                      />
                    </DisabledDayStyle>
                  </Popover>
                  {ruleErrors.find((error) => error.field === 'preOrderDate')?.msg ? (
                    <div className="mt-4">
                      <ErrorHelperText message={ruleErrors.find((error) => error.field === 'preOrderDate')?.msg} />
                    </div>
                  ) : null}
                </div>
              )}
            </BlockStack>
          </div>
        }
      />

      <EstimatedTextCard
        isRequired
        value={ruleSettings.estimatedText || ''}
        onchange={(_, styledValue) => {
          handleUpdateValue('estimatedText', styledValue);
          handleErrors('cartCustomText', styledValue?.trimStart() ? '' : 'This field is required');
        }}
        defaultError={!!ruleErrors.find((error) => error.field === 'cartCustomText') ? 'This field is required' : ''}
      />

      {!isShowCardTooltip ? (
        <>
          <div className="mt-12">
            <Button variant="primary" fullWidth icon={PlusIcon} onClick={handleAddFieldItems}>
              Add field
            </Button>
          </div>

          {isShowAddTooltipBtn ? (
            <div className="mt-12" onClick={handleAddField}>
              <Card>
                <ButtonIconVertical icon={<BubbleInfoIcon />} title="Tooltip">
                  Additional information (shipping policy, note, etc.)
                </ButtonIconVertical>
              </Card>
            </div>
          ) : null}
        </>
      ) : null}

      {isShowCardTooltip ? (
        <div className="mt-8">
          <CardTooltip onDismiss={handleDeleteField} />
        </div>
      ) : null}
    </>
  );
};

export default DateAndMessage;
